import React from 'react';
import { actorInfo } from '../mock/komalData';
import { MapPin, GraduationCap, Award } from 'lucide-react';
import OptimizedImage from './OptimizedImage';

const AboutKomal = () => {
  return (
    <section id="about" className="min-h-screen bg-[#0a0a0a] py-20 px-6 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* MOBILE: Option B - Overlapping Image with Content Card */}
        <div className="lg:hidden">
          {/* Title */}
          <div className="mb-6">
            <h2 className="font-display text-4xl font-black text-white">
              ABOUT <span className="text-[#FF6B6B]">ME</span>
            </h2>
            <div className="w-16 h-1 bg-[#FF6B6B] mt-3"></div>
          </div>

          {/* Container for overlapping effect */}
          <div className="relative">
            {/* Image at top with glow */}
            <div className="relative max-w-sm mx-auto mb-[-40px] z-10">
              <div className="absolute -inset-3 bg-gradient-to-br from-[#FF6B6B] via-[#4ECDC4] to-[#FFD700] rounded-2xl blur-xl opacity-30"></div>
              <div className="relative rounded-2xl overflow-hidden ring-2 ring-gray-800 shadow-2xl">
                <OptimizedImage
                  src={actorInfo.photos.about}
                  alt="Komal Verma"
                  className="w-full h-auto object-cover"
                  priority={true}
                />
              </div>
              {/* Height badge on image */}
              <div className="absolute bottom-3 right-3 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] text-white px-3 py-2 rounded-lg shadow-xl">
                <div className="text-xl font-black">5'6"</div>
                <div className="text-[9px] uppercase tracking-wider">Height</div>
              </div>
            </div>

            {/* Content Card that overlaps with image */}
            <div className="relative bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-2xl border border-gray-800 pt-14 p-5">
              {/* Bio */}
              <div className="space-y-3 text-gray-300 text-sm leading-relaxed mb-4">
                {actorInfo.bio.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>

              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="flex items-start gap-2 bg-[#0a0a0a] p-3 rounded-lg border border-gray-800">
                  <MapPin className="text-[#FF6B6B] flex-shrink-0 mt-0.5" size={14} />
                  <div className="min-w-0">
                    <div className="text-white font-semibold text-[10px] mb-0.5">Based In</div>
                    <div className="text-gray-400 text-[9px] leading-tight">{actorInfo.location}</div>
                    <div className="text-gray-500 text-[8px] leading-tight">Native: {actorInfo.native}</div>
                  </div>
                </div>

                <div className="flex items-start gap-2 bg-[#0a0a0a] p-3 rounded-lg border border-gray-800">
                  <GraduationCap className="text-[#4ECDC4] flex-shrink-0 mt-0.5" size={14} />
                  <div className="min-w-0">
                    <div className="text-white font-semibold text-[10px] mb-0.5">Education</div>
                    <div className="text-gray-400 text-[9px] leading-tight">SRCC, Delhi University</div>
                  </div>
                </div>
              </div>

              {/* Statement */}
              <div className="p-3 bg-gradient-to-r from-[#FF6B6B]/10 to-[#4ECDC4]/10 border-l-4 border-[#FF6B6B] rounded-lg">
                <p className="text-white text-xs font-medium italic leading-snug">
                  "I'm ready to be the kind of lead actress who holds the heartbeat of a story — 
                  bringing sincerity, strength, and grace to every frame."
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* DESKTOP: Original Layout - Unchanged */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Side */}
          <div className="order-2 lg:order-1">
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute -inset-4 bg-gradient-to-br from-[#FF6B6B] via-[#4ECDC4] to-[#FFD700] rounded-lg blur-2xl opacity-20"></div>
              
              {/* Image */}
              <div className="relative rounded-lg overflow-hidden">
                <OptimizedImage
                  src={actorInfo.photos.about}
                  alt="Komal Verma"
                  className="w-full h-auto object-cover"
                  priority={true}
                />
                />
              </div>

              {/* Floating Info Cards */}
              <div className="absolute -bottom-6 -right-6 bg-[#FF6B6B] text-white p-6 rounded-lg shadow-2xl">
                <div className="text-3xl font-black mb-1">5'6"</div>
                <div className="text-sm uppercase tracking-wider">Height</div>
              </div>
            </div>
          </div>

          {/* Content Side */}
          <div className="order-1 lg:order-2">
            <h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-black text-white mb-6">
              ABOUT <span className="text-[#FF6B6B]">ME</span>
            </h2>
            <div className="w-20 h-1 bg-[#FF6B6B] mb-8"></div>

            {/* Bio */}
            <div className="space-y-4 text-gray-300 text-base sm:text-lg leading-relaxed">
              {actorInfo.bio.split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            {/* Quick Info */}
            <div className="mt-10 grid grid-cols-2 gap-6">
              <div className="flex items-start gap-4 bg-[#1a1a1a] p-4 rounded-lg border border-gray-800 hover:border-[#FF6B6B] transition-colors">
                <MapPin className="text-[#FF6B6B] flex-shrink-0 mt-1" size={18} />
                <div className="min-w-0">
                  <div className="text-white font-semibold text-base mb-1">Based In</div>
                  <div className="text-gray-400 text-sm">{actorInfo.location}</div>
                  <div className="text-gray-500 text-sm">Native: {actorInfo.native}</div>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-[#1a1a1a] p-4 rounded-lg border border-gray-800 hover:border-[#4ECDC4] transition-colors">
                <GraduationCap className="text-[#4ECDC4] flex-shrink-0 mt-1" size={18} />
                <div className="min-w-0">
                  <div className="text-white font-semibold text-base mb-1">Education</div>
                  <div className="text-gray-400 text-sm">SRCC, Delhi University</div>
                </div>
              </div>
            </div>

            {/* Statement */}
            <div className="mt-10 p-6 bg-gradient-to-r from-[#FF6B6B]/10 to-[#4ECDC4]/10 border-l-4 border-[#FF6B6B] rounded-lg">
              <p className="text-white text-lg font-medium italic leading-relaxed">
                "I'm ready to be the kind of lead actress who holds the heartbeat of a story — 
                bringing sincerity, strength, and grace to every frame."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutKomal;
